<template>
  <a
    class="asd20-logo"
    :class="classes"
    :href="link"
    :aria-label="
      tagline
        ? `${abbreviatedTitle} ${subtitle}, An Academy District 20 School`
        : `${abbreviatedTitle} ${subtitle}`
    "
  >
    <figure class="asd20-logo__image">
      <img v-if="image" :src="image" alt="Our school logo." />
    </figure>
    <component :is="tag" class="asd20-logo__title">
      <span v-html="abbreviatedTitle"></span>
      <small v-if="subtitle" class="asd20-logo__subtitle">
        {{ subtitle }}
      </small>
      <span v-if="tagline" class="asd20-logo__tagline">
        An Academy District 20 School
        <!-- <hr />
        <i>We educate and inspire students to thrive.</i> -->
      </span>
    </component>
  </a>
</template>

<script>
export default {
  name: 'Asd20Logo',
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    tag: { type: String, default: 'span' },
    image: { type: String, default: '' },
    link: { type: String, default: '/' },
    size: {
      type: String,
      default: 'lg',
      validator: value => {
        // The value must match one of these strings
        return ['xs', 'sm', 'md', 'lg'].indexOf(value) !== -1
      },
    },
    horizontal: { type: Boolean, default: false },
    bordered: { type: Boolean, default: false },
    abbreviated: { type: Boolean, default: false },
    tagline: { type: Boolean, default: false },
    reversed: { type: Boolean, default: false },
    wrappable: { type: Boolean, default: false },
  },
  computed: {
    classes() {
      let classes = {}
      classes['asd20-logo'] = true
      classes[`asd20-logo--${this.size}`] = true
      classes[`asd20-logo--horizontal`] = this.horizontal
      classes[`asd20-logo--bordered`] = this.bordered
      classes[`asd20-logo--reversed`] = this.reversed
      classes[`asd20-logo--wrappable`] = this.wrappable
      classes[`asd20-logo--noSubtitle`] = !this.subtitle
      return classes
    },
    abbreviatedTitle() {
      let newTitle = ''
      if (this.title === 'Academy District 20 Insider') {
        return (newTitle = 'D20 Insider')
      }
      const words = this.title.trim().split(' ')
      if (this.abbreviated && words.length >= 3) {
        for (let i = 0; i < words.length; i++) {
          newTitle += `${words[i][0]}<em>${words[i].substr(1)} </em>`
        }
        return newTitle
      }
      return this.title.trim()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../design/_variables.scss';
.asd20-logo {
  --logo-border-size: 2px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  box-shadow: none;
  font-size: 2rem;
  width: max-content;
  flex-grow: 0;

  &:hover {
    box-shadow: none;
  }

  &__image {
    position: relative;
    margin: 0;
    padding: 0;
    height: 2em;
    width: auto;
    margin-right: space(0.5);
    img {
      height: 100%;
      width: auto;
      object-fit: cover;
      object-position: 50% 50%;
      display: block;
    }
  }
  &__title {
    display: flex;
    flex-direction: column;
    margin: 0;
    // text-transform: uppercase;
    font-size: 1em !important;
    font-weight: var(--website-typography__font-weight-headlines, bold);
    font-family: var(--website-typography__font-family-headlines);
    line-height: 1;
    color: var((--logo-title-color), var(--color__primary));
    // white-space: nowrap;
    max-width: 135px;
    &::v-deep em {
      display: none;
      font-style: normal;
    }
  }
  &__subtitle {
    display: block;
    font-family: var(--website-typography__font-family-body);
    font-size: 0.75em;
    text-transform: uppercase;
    font-weight: var(--website-typography__font-weight-body, normal);
    line-height: 1;
    color: var(--logo-subtitle-color, var(--color__secondary));
    opacity: 1;
  }

  &__tagline {
    width: 100%;
    color: var(--logo-subtitle-color, var(--color__secondary));
    margin-top: space(0.5);
    font-family: var(--website-typography__font-family-headlines);
    font-weight: var(--website-typography__font-weight-body, normal);
    font-size: 0.75rem;
    // text-transform: uppercase;
    // opacity: 0.75;
    // text-align: justify;
    hr {
      margin: 0.25rem 0;
      padding: 0;
      border-top: 1px solid var(--logo-subtitle-color);
    }
  }
  &--bordered {
    .asd20-logo__subtitle {
      border-top: var(--logo-border-size) solid
        var(--logo-border-color, var(--color__accent));
      margin-top: 0.125em;
      padding-top: 0.125em;
    }
  }

  &--reversed {
    .asd20-logo__image {
    }
    .asd20-logo__title {
      color: var(--logo-title-color, var(--color__on-primary));
    }
    .asd20-logo__subtitle {
      color: var(--logo-subtitle-color, var(--color__on-secondary));
    }
    .asd20-logo__tagline {
      color: var(--logo-subtitle-color, var(--color__on-secondary));
    }
  }
  &--wrappable {
    width: auto;
    .asd20-logo__title {
      width: 100%;
      margin-top: 0.5em;
      white-space: normal;
    }
  }
  &--noSubtitle {
    align-items: center;
  }
  &--md {
    font-size: 1.5rem;
  }
  &--sm {
    --logo-border-size: 1px;
    font-size: 1.25rem;
  }
  &--xs {
    --logo-border-size: 1px;
    font-size: 1rem;
    padding: 0.5rem 0;
  }
}

@media (min-width: 768px) {
  .asd20-logo {
    &__title {
      white-space: nowrap;
      max-width: inherit;
    }
  }
  .asd20-logo--wrappable {
    width: max-content;
    .asd20-logo__title {
      width: auto;
      margin-top: 0;
      white-space: nowrap;
    }
  }
}

@media (min-width: 1024px) {
  .asd20-logo--horizontal {
    .asd20-logo__title {
      flex-direction: row;
    }
    .asd20-logo__subtitle {
      font-size: 1em;
      font-weight: bold;
      font-family: var(--website-typography__font-family-headlines);
      margin: 0 0 0 0.25em;
      padding: 0;
      border: 0;
    }
  }
}
</style>
